import { formatMessage } from "./message";
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import en, { type Key } from "../locales/en";
import { dictionaries, intlTag, languageOf, languages } from "../locales/languages";

// A language code from the registry (internal/platform/i18n/languages.json).
export type Locale = string;

type Ctx = {
  locale: Locale;
  dir: "ltr" | "rtl";
  setLocale: (l: Locale) => void;
  t: (key: Key, vars?: Record<string, string | number>) => string;
  num: (n: number) => string;
  money: (n: number, currency: string) => string;
  date: (d: string | Date, opts?: Intl.DateTimeFormatOptions) => string;
  relative: (d: string | Date) => string;
  bytes: (n: number) => string;
};

const I18n = createContext<Ctx | null>(null);

function initial(): Locale {
  try {
    const saved = localStorage.getItem("sooquna.locale");
    if (saved && languageOf(saved)) return saved;
  } catch {
    /* storage unavailable */
  }
  return "en"; // another language only once chosen with the language switch
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(initial);
  const dir = languageOf(locale)?.dir ?? "ltr";

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  const value = useMemo<Ctx>(() => {
    const tag = intlTag(locale);
    const nf = new Intl.NumberFormat(tag);
    const rtf = new Intl.RelativeTimeFormat(tag, { numeric: "auto" });
    return {
      locale,
      dir,
      setLocale: (l) => {
        try {
          localStorage.setItem("sooquna.locale", l);
        } catch {
          /* storage unavailable */
        }
        // Set now, not in the effect: queries refetching for the new
        // language send it as Accept-Language (lib/api).
        document.documentElement.lang = l;
        setLocaleState(l);
      },
      // A language without a message shows English (new languages may be partial).
      t: (key, vars) => formatMessage(dictionaries[locale]?.[key] ?? en[key] ?? key, vars, locale),
      num: (n) => nf.format(n),
      money: (n, currency) => new Intl.NumberFormat(tag, { style: "currency", currency }).format(n),
      date: (d, opts = { dateStyle: "medium" }) => new Intl.DateTimeFormat(tag, opts).format(new Date(d)),
      relative: (d) => {
        const secs = (new Date(d).getTime() - Date.now()) / 1000;
        const units: [Intl.RelativeTimeFormatUnit, number][] = [["day", 86400], ["hour", 3600], ["minute", 60]];
        for (const [u, s] of units) if (Math.abs(secs) >= s) return rtf.format(Math.round(secs / s), u);
        return rtf.format(Math.round(secs), "second");
      },
      bytes: (n) => {
        const units = ["B", "KB", "MB", "GB", "TB"];
        let i = 0;
        while (n >= 1024 && i < units.length - 1) (n /= 1024), i++;
        return `${nf.format(Math.round(n * 10) / 10)} ${units[i]}`;
      },
    };
  }, [locale, dir]);

  return <I18n.Provider value={value}>{children}</I18n.Provider>;
}

export { languages };

export function useI18n() {
  const c = useContext(I18n);
  if (!c) throw new Error("useI18n outside I18nProvider");
  return c;
}
